import React from "react";
import { useAnketaI18n } from "src/internationalization";

/*
 * Integrations wall - placed directly under the hero (Jakob's Law: the
 * "works with" strip sits where competitor security sites put it).
 *
 * opsanio's value is that it reads your EXISTING stack rather than replacing it.
 * To keep the "40+ native integrations" claim HONEST, this lists the actual
 * scanner/connector integrations by name (the authoritative set the product's
 * own login screen advertises) as a continuous marquee - rather than a curated
 * logo wall that didn't match the claim. Names are product/standard terms and
 * are not translated.
 */

const tPrefix = "anketa.autonubil.home.integrations";

// Authoritative integration list: the dashboard scanners plus the streamer plugins.
const INTEGRATIONS = [
  "Network Scan", "Proxmox", "VMware vCenter", "OpenNebula", "Azure Local",
  "Nutanix", "Kubernetes", "Microsoft Entra ID", "Microsoft Intune",
  "Microsoft Defender", "Microsoft Graph", "Office 365", "Active Directory",
  "Keycloak", "Windows (WinRM)", "Wazuh", "SentinelOne", "Cortex XDR",
  "Arctic Wolf", "Menlo Security", "Fortinet", "SonicWall", "Sophos", "Nessus",
  "Nuclei", "PRTG", "Zabbix", "Icinga 2", "Checkmk", "Netdata", "NetBox",
  "i-doit", "baramundi", "NinjaOne", "HPE Aruba Central", "HPE GreenLake",
  "Octopus Deploy", "endoflife.date", "IMAP", "RSS Feed", "Threat Intel Feeds",
  "HPE Alletra / Primera / 3PAR", "Redfish (BMC)",
  // streamer plugins
  "OpenVAS (Greenbone)", "Trivy (SBOM)", "Grafana Loki", "SNMP Traps", "PCAP", "Web Traffic (Traefik)", "Webhooks"
];

// Seconds per chip, so the scroll speed stays constant as integrations are added.
const SPEED_S = Math.round(INTEGRATIONS.length * 1.7);

export const AutonubilIntegrations: React.FC = () => {
  const { translate } = useAnketaI18n();
  const loop = [...INTEGRATIONS, ...INTEGRATIONS];

  return (
    <section
      id="integrations"
      style={{
        background: "var(--ak-surface)",
        borderBottom: "1px solid var(--ak-border)",
        padding: "44px clamp(24px, 5vw, 48px) 40px",
      }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <p
          style={{
            textAlign: "center",
            margin: 0,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "var(--ak-text-muted)",
          }}
        >
          {translate(`${tPrefix}.eyebrow`, { defaultMessage: "Works with your existing stack" } as any)}
        </p>
        <h2
          style={{
            textAlign: "center",
            margin: "10px 0 0",
            fontSize: "clamp(1.4rem, 2.4vw, 1.9rem)",
            fontWeight: 800,
            letterSpacing: "-0.02em",
            color: "var(--ak-text)",
          }}
        >
          {translate(`${tPrefix}.title.pre`, { defaultMessage: "One platform," } as any)}{" "}
          <span
            style={{
              background: "linear-gradient(120deg, var(--ak-accent) 0%, var(--ak-accent-2) 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {translate(`${tPrefix}.title.highlight`, { defaultMessage: "40+ native integrations." } as any)}
          </span>
        </h2>
        <p
          style={{
            textAlign: "center",
            margin: "12px auto 0",
            maxWidth: 640,
            fontSize: "1rem",
            lineHeight: 1.6,
            color: "var(--ak-text-secondary)",
          }}
        >
          {translate(`${tPrefix}.subtitle`, {
            defaultMessage:
              "opsanio reads the tools you already run - virtualisation, endpoints, identity, network and security - and merges them into one sovereign data model. No rip-and-replace.",
          } as any)}
        </p>
      </div>

      <style>
        {`
          @keyframes ak-int-scroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
          .ak-int-wrap { position: relative; width: 100%; margin-top: 28px; overflow: hidden; }
          .ak-int-wrap::before, .ak-int-wrap::after {
            content: ''; position: absolute; top: 0; bottom: 0; width: 120px; z-index: 2; pointer-events: none;
          }
          .ak-int-wrap::before { left: 0; background: linear-gradient(to right, var(--ak-surface), transparent); }
          .ak-int-wrap::after { right: 0; background: linear-gradient(to left, var(--ak-surface), transparent); }
          .ak-int-track { display: flex; width: max-content; gap: 12px; animation: ak-int-scroll ${SPEED_S}s linear infinite; }
          .ak-int-wrap:hover .ak-int-track { animation-play-state: paused; }
          @media (prefers-reduced-motion: reduce) {
            .ak-int-track { animation: none; flex-wrap: wrap; width: 100%; justify-content: center; }
          }
          .ak-int-chip {
            flex-shrink: 0; display: inline-flex; align-items: center; gap: 8px;
            padding: 9px 16px; border-radius: 999px; font-size: 13.5px; font-weight: 600;
            color: var(--ak-text-secondary); background: var(--ak-surface-2);
            border: 1px solid var(--ak-border); white-space: nowrap;
          }
          .ak-int-chip span.dot { width: 6px; height: 6px; border-radius: 999px; background: var(--ak-accent); opacity: .7; }
        `}
      </style>

      <div className="ak-int-wrap" aria-label={translate(`${tPrefix}.aria`, { defaultMessage: "Integrated systems" } as any)}>
        <div className="ak-int-track">
          {loop.map((name, i) => (
            <span className="ak-int-chip" key={`${name}-${i}`} aria-hidden={i >= INTEGRATIONS.length}>
              <span className="dot" />
              {name}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AutonubilIntegrations;
